/**
 * Magic Link Service
 *
 * API service for generating and managing magic links for estimates/invoices.
 * Part of Batch 5c migration.
 *
 * Platform-agnostic - uses adapters for environment variables and clipboard.
 */

import { getEnv, getRequiredEnv } from '../adapters'
import { copyToClipboard } from '../adapters'
import { MAGIC_LINK_CACHE_EXPIRY, DEFAULT_MAGIC_LINK_EXPIRY_DAYS } from './constants'

/**
 * Get the API base URL for magic link endpoints
 * @returns {string} API base URL
 */
const getApiBaseUrl = () => {
  const supabaseUrl = getRequiredEnv('SUPABASE_URL')
  return getEnv('API_BASE') || getEnv('API_URL') || `${supabaseUrl}/functions/v1`
}

/**
 * Get the Supabase anon key for API authorization
 * @returns {string} Anon key
 */
const getAnonKey = () => {
  return getRequiredEnv('SUPABASE_ANON_KEY')
}

/**
 * Magic Link Service class
 * Handles generation, validation, and management of magic links
 */
export class MagicLinkService {
  // In-memory cache for magic links to avoid repeated API calls
  static linkCache = new Map()
  static cacheExpiry = MAGIC_LINK_CACHE_EXPIRY

  /**
   * Get cache key for magic link lookup
   * @param {string} type - Entity type (estimate/invoice)
   * @param {string} entityId - Entity ID
   * @param {string} companyId - Company ID
   * @returns {string} Cache key
   */
  static getCacheKey(type, entityId, companyId) {
    return `${type}-${entityId}-${companyId}`
  }

  /**
   * Clear cached magic link for an entity
   * @param {string} type - Entity type
   * @param {string} entityId - Entity ID
   * @param {string} companyId - Company ID
   */
  static clearCache(type, entityId, companyId) {
    const cacheKey = this.getCacheKey(type, entityId, companyId)
    this.linkCache.delete(cacheKey)
  }

  /**
   * Clear all cached magic links
   */
  static clearAllCache() {
    this.linkCache.clear()
  }

  /**
   * Get existing magic link for an estimate or invoice
   * @param {string} type - Entity type (estimate/invoice)
   * @param {string} entityId - Entity ID
   * @param {string} companyId - Company ID
   * @returns {Promise<object|null>} Magic link object or null
   */
  static async getExistingMagicLink(type, entityId, companyId) {
    // Check in-memory cache first
    const cacheKey = this.getCacheKey(type, entityId, companyId)
    const cached = this.linkCache.get(cacheKey)

    if (cached && cached.timestamp > Date.now() - this.cacheExpiry) {
      console.log('[MagicLinkService] Using cached magic link:', cached.link)
      return cached.link
    }

    try {
      const apiBaseUrl = getApiBaseUrl()
      const endpoint = `${apiBaseUrl}/magic-link-get-existing`

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAnonKey()}`
        },
        body: JSON.stringify({
          type,
          entityId,
          companyId,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get existing magic link')
      }

      // Cache the result if we got a valid magic link
      if (data.magicLink) {
        this.linkCache.set(cacheKey, {
          link: data.magicLink,
          timestamp: Date.now()
        })
      }

      return data.magicLink
    } catch (error) {
      console.error('[MagicLinkService] Error getting existing magic link:', error)
      // If the function doesn't exist yet, return null instead of throwing
      if (error.message.includes('CORS') || error.message.includes('ERR_FAILED')) {
        console.log('[MagicLinkService] Magic link get-existing function not deployed yet, falling back to generate new')
        return null
      }
      throw error
    }
  }

  /**
   * Generate a magic link for an estimate or invoice
   * This will check for existing links first and only generate a new one if needed
   * @param {string} type - Entity type (estimate/invoice)
   * @param {string} entityId - Entity ID
   * @param {string} companyId - Company ID
   * @param {number} expiresInDays - Days until link expires
   * @param {boolean} forceNew - Force generation of new link
   * @returns {Promise<object>} Magic link object
   */
  static async generateMagicLink(type, entityId, companyId, expiresInDays = DEFAULT_MAGIC_LINK_EXPIRY_DAYS, forceNew = false) {
    try {
      const apiBaseUrl = getApiBaseUrl()

      console.log(`[MagicLinkService] generateMagicLink called:`, {
        type,
        entityId,
        companyId,
        expiresInDays,
        forceNew,
        apiBaseUrl
      })

      // First check if there's an existing valid magic link
      if (!forceNew) {
        try {
          console.log(`[MagicLinkService] Checking for existing magic link...`)
          const existingLink = await this.getExistingMagicLink(type, entityId, companyId)
          if (existingLink && !this.isExpired(existingLink.expiresAt)) {
            console.log('[MagicLinkService] Using existing magic link:', existingLink)
            return existingLink
          } else if (existingLink && this.isExpired(existingLink.expiresAt)) {
            console.log('[MagicLinkService] Existing magic link is expired, generating new one')
          }
        } catch (error) {
          console.log('[MagicLinkService] No existing magic link found, generating new one:', error.message)
        }
      } else {
        console.log('[MagicLinkService] forceNew=true, skipping existing link check')
      }

      const endpoint = `${apiBaseUrl}/magic-link-generate`
      console.log('[MagicLinkService] Making request to edge function:', endpoint)
      console.log('[MagicLinkService] Request payload:', {
        type,
        entityId,
        companyId,
        expiresInDays
      })

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAnonKey()}`
        },
        body: JSON.stringify({
          type,
          entityId,
          companyId,
          expiresInDays,
        }),
      })

      console.log('[MagicLinkService] Response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorText = await response.text()
        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { error: errorText || 'Unknown error' }
        }
        console.error('[MagicLinkService] Edge function error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        })
        throw new Error(errorData.error || `Failed to generate magic link: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      console.log('[MagicLinkService] Edge function response:', {
        success: data.success,
        hasMagicLink: !!data.magicLink,
        magicLinkUrl: data.magicLink?.url
      })

      if (!data.magicLink) {
        throw new Error('Edge function returned success but no magic link in response')
      }

      // Cache the newly generated magic link
      const cacheKey = this.getCacheKey(type, entityId, companyId)
      this.linkCache.set(cacheKey, {
        link: data.magicLink,
        timestamp: Date.now()
      })
      console.log('[MagicLinkService] Cached magic link:', cacheKey)

      return data.magicLink
    } catch (error) {
      console.error('[MagicLinkService] Error generating magic link:', {
        message: error.message,
        stack: error.stack,
        type,
        entityId,
        companyId
      })
      throw error
    }
  }

  /**
   * Check if a magic link is expired
   * @param {string} expiresAt - ISO date string
   * @returns {boolean} True if expired
   */
  static isExpired(expiresAt) {
    if (!expiresAt) return true
    return new Date(expiresAt) < new Date()
  }

  /**
   * Validate a magic link token
   * @param {string} token - Magic link token
   * @returns {Promise<object>} Validation result
   */
  static async validateMagicLink(token) {
    try {
      const apiBaseUrl = getApiBaseUrl()
      const endpoint = `${apiBaseUrl}/magic-link-validate`

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAnonKey()}`
        },
        body: JSON.stringify({ token }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to validate magic link')
      }

      return data
    } catch (error) {
      console.error('[MagicLinkService] Error validating magic link:', error)
      throw error
    }
  }

  /**
   * Record a client response
   * @param {string} token - Magic link token
   * @param {object} responseData - Response data
   * @returns {Promise<object>} Result
   */
  static async recordClientResponse(token, responseData) {
    try {
      const apiBaseUrl = getApiBaseUrl()
      const endpoint = `${apiBaseUrl}/magic-link-respond`

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAnonKey()}`
        },
        body: JSON.stringify({
          token,
          ...responseData,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to record response')
      }

      return data
    } catch (error) {
      console.error('[MagicLinkService] Error recording client response:', error)
      throw error
    }
  }

  /**
   * Get magic link statistics
   * @param {string} companyId - Company ID
   * @returns {Promise<object>} Stats
   */
  static async getMagicLinkStats(companyId) {
    try {
      const apiBaseUrl = getApiBaseUrl()
      const endpoint = `${apiBaseUrl}/magic-link-stats`

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAnonKey()}`
        },
        body: JSON.stringify({ companyId }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get statistics')
      }

      return data.stats
    } catch (error) {
      console.error('[MagicLinkService] Error getting magic link stats:', error)
      throw error
    }
  }

  /**
   * Revoke a magic link
   * @param {string} token - Magic link token
   * @returns {Promise<object>} Result
   */
  static async revokeMagicLink(token) {
    try {
      const apiBaseUrl = getApiBaseUrl()
      const endpoint = `${apiBaseUrl}/magic-link-revoke`

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAnonKey()}`
        },
        body: JSON.stringify({ token }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to revoke magic link')
      }

      return data
    } catch (error) {
      console.error('[MagicLinkService] Error revoking magic link:', error)
      throw error
    }
  }

  /**
   * Get client responses for an entity
   * @param {string} type - Entity type
   * @param {string} entityId - Entity ID
   * @param {string} companyId - Company ID
   * @returns {Promise<object[]>} Responses
   */
  static async getClientResponses(type, entityId, companyId) {
    try {
      const apiBaseUrl = getApiBaseUrl()
      const endpoint = `${apiBaseUrl}/magic-link-responses`

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAnonKey()}`
        },
        body: JSON.stringify({ type, entityId, companyId }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get responses')
      }

      return data.responses
    } catch (error) {
      console.error('[MagicLinkService] Error getting client responses:', error)
      throw error
    }
  }

  /**
   * Copy magic link to clipboard
   * Uses the clipboard adapter for cross-platform support
   * @param {string} url - URL to copy
   * @returns {Promise<boolean>} True if successful
   */
  static async copyToClipboard(url) {
    return copyToClipboard(url)
  }

  /**
   * Generate QR code URL for magic link
   * @param {string} url - Magic link URL
   * @param {number} size - QR code size in pixels
   * @returns {string} QR code image URL
   */
  static generateQRCodeUrl(url, size = 200) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`
  }
}

// Export convenience functions for direct use
export const generateMagicLink = MagicLinkService.generateMagicLink.bind(MagicLinkService)
export const validateMagicLink = MagicLinkService.validateMagicLink.bind(MagicLinkService)
export const revokeMagicLink = MagicLinkService.revokeMagicLink.bind(MagicLinkService)
export const getExistingMagicLink = MagicLinkService.getExistingMagicLink.bind(MagicLinkService)
export const recordClientResponse = MagicLinkService.recordClientResponse.bind(MagicLinkService)
export const getMagicLinkStats = MagicLinkService.getMagicLinkStats.bind(MagicLinkService)
export const getClientResponses = MagicLinkService.getClientResponses.bind(MagicLinkService)
export const generateQRCodeUrl = MagicLinkService.generateQRCodeUrl.bind(MagicLinkService)
